import React, { useEffect, useState } from 'react';
import { getData, postData } from '../../../webService/webservice.js';
import { toast } from 'react-toastify';
import '../../../styles/Auth/payment.css';
import '../../../styles/Auth/store.css';
import '../../../styles/Auth/product.css';
import { Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper, Typography, Box } from '@mui/material';
import { Icon } from '@iconify/react';
import TVFMLoader from '../Loader/loader.jsx'; // Will create this

const PendingPayments = () => {
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Pagination & Filters State
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [sortField, setSortField] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState(-1);

    const token = localStorage.getItem("Token");

    
    const handleSort = (field) => {
        if (sortField === field) {
            setSortOrder(sortOrder === 1 ? -1 : 1);
        } else {
            setSortField(field);
            setSortOrder(1);
        }
    };

    const fetchPendingPayments = async () => {
        setLoading(true);
        const res = await getData('payments/manual/all', {}, token);
        if (res?.success) {
            setPayments(res.data || []);
        } else {
            toast.error(res?.message || "Failed to fetch pending payments");
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchPendingPayments();
    }, []);

    const handleApprove = async (id) => {
        if (!window.confirm("Are you sure you want to approve this payment?")) return;
        const res = await postData('payments/manual/approve', { paymentId: id }, token);
        if (res?.success) {
            toast.success("Payment approved successfully!");
            fetchPendingPayments();
        } else {
            toast.error(res?.message || "Failed to approve payment");
        }
    };

    const handleReject = async (id) => {
        const reason = window.prompt("Enter rejection reason:");
        if (reason === null) return;
        const res = await postData('payments/manual/reject', { paymentId: id, reason }, token);
        if (res?.success) {
            toast.success("Payment rejected.");
            fetchPendingPayments();
        } else {
            toast.error(res?.message || "Failed to reject payment");
        }
    };

    // Computed derived states
    const filteredPayments = payments.filter(payment => {
        
        const matchesStatus = statusFilter === 'ALL' || payment.paymentStatus === statusFilter;
        const matchesSearch = searchTerm === '' || 
            (payment.storeId?.storeName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (payment.packageName?.toLowerCase() || payment.packageId?.packageName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (payment.paymentReference?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (payment.transactionId?.toLowerCase() || '').includes(searchTerm.toLowerCase());
        
        return matchesStatus && matchesSearch;
    }).sort((a, b) => {
        let valA, valB;
        switch (sortField) {
            case 'store':
                valA = a.storeId?.storeName || '';
                valB = b.storeId?.storeName || '';
                break;
            case 'package':
                valA = a.packageName || a.packageId?.packageName || '';
                valB = b.packageName || b.packageId?.packageName || '';
                break;
            case 'paymentMode':
                valA = a.paymentMode || '';
                valB = b.paymentMode || '';
                break;
            case 'amount':
                valA = a.totalAmount || 0;
                valB = b.totalAmount || 0;
                break;
            case 'reference':
                valA = a.paymentReference || a.transactionId || '';
                valB = b.paymentReference || b.transactionId || '';
                break;
            case 'status':
                valA = a.paymentStatus || '';
                valB = b.paymentStatus || '';
                break;
            default:
                valA = a.createdAt || '';
                valB = b.createdAt || '';
        }
        
        if (typeof valA === 'string') valA = valA.toLowerCase();
        if (typeof valB === 'string') valB = valB.toLowerCase();
        
        if (valA < valB) return -1 * sortOrder;
        if (valA > valB) return 1 * sortOrder;
        return 0;
    });

    const totalPages = limit === 'all' ? 1 : Math.ceil(filteredPayments.length / limit);
    const paginatedPayments = limit === 'all' 
        ? filteredPayments 
        : filteredPayments.slice((currentPage - 1) * limit, currentPage * limit);

    const handleItemsPerPageChange = (e) => {
        setLimit(e.target.value === 'all' ? 'all' : parseInt(e.target.value, 10));
        setCurrentPage(1);
    };

    // Reset to page 1 if filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, statusFilter]);

    
    const headings = [
        { label: 'Store', field: 'store', sortable: true },
        { label: 'Package', field: 'package', sortable: true },
        { label: 'Payment Mode', field: 'paymentMode', sortable: true },
        { label: 'Amount', field: 'amount', sortable: true },
        { label: 'Reference No', field: 'reference', sortable: true },
        { label: 'Status', field: 'status', sortable: true },
        { label: 'Action', field: 'action', sortable: false }
    ];

    return (
        <div className="product-layout">
            <div className="product-content">
                {loading && (
                    <div className="scissor-overlay">
                        <div className="scissor-loader">
                            <TVFMLoader />
                        </div>
                    </div>
                )}
                <main className="product-main">
                    <Typography variant="h6" mb={2}>Manual Package Payments</Typography>
                    
                    <div className="product-widgets">
                        <div className="search-input-wrapper">
                            <input
                                type="text"
                                placeholder="Search by store, package or reference..."
                                className="store-search-input"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <Icon icon="mdi:magnify" className="search-icon" />
                        </div>
                        <select 
                            value={statusFilter} 
                            onChange={(e) => setStatusFilter(e.target.value)}
                            style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ccc', outline: 'none' }}
                        >
                            <option value="ALL">All Status</option>
                            <option value="PENDING">Pending</option>
                            <option value="SUCCESS">Success</option>
                            <option value="REJECTED">Rejected</option>
                        </select>
                    </div>

                    <Box component={Paper}>
                        <TableContainer sx={{
                            overflow: 'auto',
                            maxHeight: '450px',
                            '&::-webkit-scrollbar': { display: 'none' },
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none',
                        }}>
                            <Table stickyHeader className="custom-product-table" sx={{ minWidth: 650 }} aria-label="manual payments table">
                                
                                <TableHead className="custom-product-head">
                                    <TableRow>
                                        {headings.map((heading, index) => (
                                            <TableCell 
                                                key={index} 
                                                align={heading.field === 'action' ? 'center' : 'left'}
                                                sx={{ color: '#fff', backgroundColor: '#3C0008' }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: heading.field === 'action' ? "center" : "flex-start",
                                                        gap: "4px",
                                                        color: "#ffffff",
                                                        fontSize: "14px",
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {heading.label}
                                                    {heading.sortable && (
                                                        <span
                                                            onClick={() => handleSort(heading.field)}
                                                            style={{
                                                                fontSize: "10px",
                                                                color: "#ffffffff",
                                                                cursor: "pointer",
                                                                marginLeft: "4px",
                                                                userSelect: "none",
                                                            }}
                                                        >
                                                            {sortField === heading.field
                                                                ? sortOrder === 1 ? "▲" : "▼"
                                                                : "▲▼"}
                                                        </span>
                                                    )}
                                                </Typography>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {payments.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7}>
                                                <Typography sx={{ textAlign: 'center', padding: '20px', fontWeight: 500 }}>
                                                    No manual payments found.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : filteredPayments.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7}>
                                                <Typography sx={{ textAlign: 'center', padding: '20px', fontWeight: 500 }}>
                                                    No payments match your filters.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        paginatedPayments.map((payment) => (
                                            <TableRow key={payment._id}>
                                                <TableCell>{payment.storeId?.storeName || 'N/A'}</TableCell>
                                                <TableCell>{payment.packageName || payment.packageId?.packageName || 'N/A'}</TableCell>
                                                <TableCell>{payment.paymentMode}</TableCell>
                                                <TableCell>₹{payment.totalAmount?.toLocaleString('en-IN')}</TableCell>
                                                <TableCell>
                                                    {payment.paymentReference || payment.transactionId || 'N/A'}
                                                    {payment.paymentProofUrl && (
                                                        <div style={{ marginTop: '5px' }}>
                                                            <a 
                                                                href={payment.paymentProofUrl.startsWith('http') ? payment.paymentProofUrl : `${process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/api\/v1\/?$/, '') : ''}${payment.paymentProofUrl}`} 
                                                                target="_blank" 
                                                                rel="noreferrer" 
                                                                style={{ fontSize: '12px', color: 'blue' }}
                                                            >
                                                                View Proof
                                                            </a>
                                                        </div>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <span className={`status-badge ${payment.paymentStatus.toLowerCase()}`}>
                                                        {payment.paymentStatus}
                                                    </span>
                                                </TableCell>
                                                <TableCell align="center">
                                                    {payment.paymentStatus === 'PENDING' ? (
                                                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                                            <button className="btn-approve" onClick={() => handleApprove(payment._id)}>Approve</button>
                                                            <button className="btn-reject" onClick={() => handleReject(payment._id)}>Reject</button>
                                                        </div>
                                                    ) : (
                                                        <span style={{ fontSize: '13px', color: '#64748b' }}>No Action Needed</span>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        
                        <div className="product-bottom">
                            <div className="store-section show-entries">
                                <span className="text-label">Show</span>
                                <select className="entries-select" value={limit} onChange={handleItemsPerPageChange}>
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value="all">All</option>
                                </select>
                                <span className="text-label">Entries</span>
                            </div>
                            <div className="product-section showing-range">
                                {filteredPayments.length === 0 
                                    ? "Showing 0 to 0 of 0 entries" 
                                    : limit === 'all' 
                                        ? `Showing all ${filteredPayments.length} entries`
                                        : `Showing ${(currentPage - 1) * limit + 1} to ${Math.min(currentPage * limit, filteredPayments.length)} of ${filteredPayments.length} entries`}
                            </div>
                            <div className="product-section spacer"></div>
                            <div className="product-section pagination-buttons">
                                <button
                                    className="pagination-btn"
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage(1)}
                                >
                                    <Icon icon="mdi:chevron-double-left" />
                                </button>
                                <button
                                    className="pagination-btn"
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                >
                                    <Icon icon='mdi:chevron-left' />
                                </button>
                                <button
                                    className="pagination-btn"
                                    disabled={currentPage === totalPages || totalPages === 0}
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                >
                                    <Icon icon='mdi:chevron-right' />
                                </button>
                                <button
                                    className="pagination-btn"
                                    disabled={currentPage === totalPages || totalPages === 0}
                                    onClick={() => setCurrentPage(totalPages)}
                                >
                                    <Icon icon='mdi:chevron-double-right' />
                                </button>
                            </div>
                        </div>
                    </Box>
                </main>
            </div>
        </div>
    );
};

export default PendingPayments;
